import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import router from "next/router";
import { useEffect } from "react";
import { useUser } from "@/auth/useUser";
import { DrawerLayout } from "@/components/common/drawerLayout";
import SuccessCircleIconSvg from "@/components/common/icons/successCircleIconSvg";
import { GREY } from "@/config/mui/colorPalette";
import useErrorLogger from "@/utils/useErrorLogger";
import { useMultiProviderAppointmentDrawerContext } from "../multiProviderAppointmentDrawerContext";
import { SuccessScreenAppointmentServices } from "./successScreenAppointmentServices";
import { SuccessScreenBasicInformation } from "./successScreenBasicInformation";

type Props = {
  createdAppointmentId: string;
  onCloseDrawer: () => void;
};

export const MultiProviderAppointmentDrawerCreateSuccess = ({
  createdAppointmentId,
  onCloseDrawer,
}: Props) => {
  const { appointmentServices, isRebooking } =
    useMultiProviderAppointmentDrawerContext();

  const { medspa } = useUser();
  const logError = useErrorLogger();

  useEffect(() => {
    if (!appointmentServices.length) {
      logError(
        new Error(
          "[MultiProviderAppointmentDrawerCreateSuccess] No appointment services."
        )
      );
    }
  }, [appointmentServices, logError]);

  if (!appointmentServices.length) {
    return null;
  }

  const handleGoToAppointment = () => {
    if (!createdAppointmentId) {
      return;
    }

    onCloseDrawer();
    router
      .push(`/${medspa}/appointments/${createdAppointmentId}`)
      .catch((error) => {
        logError(error, "Error navigating to appointment");
      });
  };

  return (
    <DrawerLayout
      headerProps={{
        title: isRebooking
          ? "Rebook client appointment"
          : "Create client appointment",
      }}
      content={
        <Stack
          direction="column"
          sx={{
            alignItems: "center",
            width: "100%",
            backgroundColor: GREY[5],
            py: 2,
            px: 1.5,
            borderRadius: 2,
          }}
        >
          <SuccessCircleIconSvg size="72px" />
          <Typography
            variant="labelDefault"
            color="text.secondary"
            sx={{ mt: 2 }}
          >
            Success!
          </Typography>
          <Typography variant="h3" sx={{ mt: 0.5 }}>
            {isRebooking
              ? "Appointment has been rebooked"
              : "Appointment has been booked"}
          </Typography>
          <Divider
            orientation="horizontal"
            variant="fullWidth"
            flexItem
            sx={{ my: 3 }}
          />
          <SuccessScreenBasicInformation />
          <Box sx={{ py: 0.75 }} />
          <SuccessScreenAppointmentServices />
        </Stack>
      }
      actions={
        <>
          <Button
            onClick={handleGoToAppointment}
            variant="outlined"
            sx={{ flex: 1 }}
            size="small"
          >
            Go to appointment
          </Button>
          <Button
            onClick={onCloseDrawer}
            variant="contained"
            sx={{ flex: 1 }}
            size="small"
          >
            Close
          </Button>
        </>
      }
    />
  );
};
